import React from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TableSortLabel,
  Tooltip,
  Avatar,
  Menu,
  Backdrop,
  Divider,
  Chip,
} from '@mui/material';
import {
  KeyboardArrowDown,
  Search,
  MoreVert,
  PictureAsPdf,
  Visibility,
  CheckCircle,
  DoNotDisturbOn,
  ErrorOutline,
  InfoOutlined,
  ReportProblem,
  ContentCopy,
  Close,
  DragIndicator,
} from '@mui/icons-material';
import UnfoldMore from '@mui/icons-material/UnfoldMore';
import { useNavigate } from 'react-router-dom';
import { getAllProjects } from '../services/projectService';
import { useAuth } from '../contexts/AuthContext';
import AddExpenseDialog from '../components/expenses/AddExpenseDialog';
import InitialBalanceSettingsDrawer from '../components/expenses/InitialBalanceSettingsDrawer';

type Expense = {
  id: string;
  document_date: string; // ISO - data dokumentu
  received_date: string; // ISO - data otrzymania
  created_at: string; // ISO - data wprowadzenia
  project_id: string;
  project_name: string;
  contractor_name: string;
  net_amount: number;
  gross_amount: number;
  remarks?: string; // Uwagi
  status: 'pending' | 'approved' | 'rejected';
  google_drive_file_url?: string;
};

const currency = (value: number) =>
  new Intl.NumberFormat('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value) + ' zł';

type Project = {
  project_id: string;
  project_name: string;
};

const ExpensesPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchText, setSearchText] = React.useState('');
  const [projectFilter, setProjectFilter] = React.useState('');
  const [dateFrom, setDateFrom] = React.useState('');
  const [dateTo, setDateTo] = React.useState('');
  const [projects, setProjects] = React.useState<Project[]>([]);
  const [expensesTab, setExpensesTab] = React.useState<number>(0); // 0=Wszystkie, 1=Do uzgodnienia, 2=Uzgodnione
  const [menuAnchor, setMenuAnchor] = React.useState<null | HTMLElement>(null);
  const [menuRow, setMenuRow] = React.useState<Expense | null>(null);
  const [menuPosition, setMenuPosition] = React.useState<{ top: number; left: number } | null>(null);
  const [addExpenseDialogOpen, setAddExpenseDialogOpen] = React.useState(false);
  const [initialBalanceSettingsOpen, setInitialBalanceSettingsOpen] = React.useState(false);
  const [initialBalance, setInitialBalance] = React.useState<number>(0);
  const [initialBalanceDate, setInitialBalanceDate] = React.useState<string>('');
  const startDateRef = React.useRef<HTMLInputElement>(null);
  const endDateRef = React.useRef<HTMLInputElement>(null);
  const tableContainerRef = React.useRef<HTMLDivElement | null>(null);

  // Kolumny: tryb zmiany szerokości + zapamiętywanie per użytkownik
  const [colResizeMode, setColResizeMode] = React.useState<boolean>(false);
  const [dragCol, setDragCol] = React.useState<{ key: string; startX: number; startW: number } | null>(null);
  const defaultColumnWidths = React.useMemo<Record<string, number>>(() => ({
    dates: 176,
    project: 220,
    contractor: 150,
    net: 100,
    gross: 100,
    remarks: 200,
    approvals: 160,
    preview: 44,
    actions: 44,
  }), []);
  const STORAGE_KEY = React.useMemo(() => `expensesTableColWidths:${user?.user_id || 'anon'}`, [user?.user_id]);
  const [columnWidths, setColumnWidths] = React.useState<Record<string, number>>(defaultColumnWidths);
  const getColW = (key: string) => `${columnWidths[key] ?? defaultColumnWidths[key] ?? 40}px`;

  // Dostosuj kolumny – widoczność
  const DEFAULT_VIS: Record<string, boolean> = React.useMemo(() => ({
    dates: true, project: true, contractor: true, net: true, gross: true, remarks: true,
    approvals: true, preview: true, actions: true
  }), []);
  const VISIBLE_KEY = React.useMemo(() => `expenses.columns.visible:${user?.user_id || 'anon'}`, [user?.user_id]);
  const [columnsVisible, setColumnsVisible] = React.useState<Record<string, boolean>>(() => {
    try {
      const raw = localStorage.getItem(VISIBLE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        // Upewnij się, że wszystkie kolumny z DEFAULT_VIS są obecne
        return { ...DEFAULT_VIS, ...parsed };
      }
    } catch {}
    return { ...DEFAULT_VIS };
  });
  const colV = React.useCallback((key: string) => columnsVisible[key] !== false, [columnsVisible]);
  
  // Upewnij się, że kolumna remarks jest zawsze widoczna przy pierwszym załadowaniu
  React.useEffect(() => {
    if (columnsVisible.remarks === false || !('remarks' in columnsVisible)) {
      setColumnsVisible((prev) => ({ ...prev, remarks: true }));
    }
    if (!columnOrder.includes('remarks')) {
      const grossIndex = columnOrder.indexOf('gross');
      if (grossIndex >= 0) {
        const newOrder = [...columnOrder];
        newOrder.splice(grossIndex + 1, 0, 'remarks');
        setColumnOrder(newOrder);
      }
    }
  }, []);
  
  // Kolejność kolumn
  const COLUMNS_ORDER_DEFAULT: string[] = ['dates', 'project', 'contractor', 'net', 'gross', 'remarks', 'approvals', 'preview', 'actions'];
  const ORDER_KEY = React.useMemo(() => `expenses.columns.order:${user?.user_id || 'anon'}`, [user?.user_id]);
  const [columnOrder, setColumnOrder] = React.useState<string[]>(() => {
    try {
      const raw = localStorage.getItem(ORDER_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        // Upewnij się, że wszystkie kolumny z DEFAULT są obecne
        const missing = COLUMNS_ORDER_DEFAULT.filter(k => !parsed.includes(k));
        let result = [...parsed, ...missing];
        // Upewnij się, że 'remarks' jest za 'gross'
        const grossIndex = result.indexOf('gross');
        const remarksIndex = result.indexOf('remarks');
        if (grossIndex >= 0 && remarksIndex >= 0 && remarksIndex !== grossIndex + 1) {
          // Usuń 'remarks' z obecnej pozycji
          result = result.filter(k => k !== 'remarks');
          // Wstaw 'remarks' za 'gross'
          result.splice(grossIndex + 1, 0, 'remarks');
        }
        return result;
      }
    } catch {}
    return [...COLUMNS_ORDER_DEFAULT];
  });
  
  // Panel kolumn
  const [colsAnchorEl, setColsAnchorEl] = React.useState<null | HTMLElement>(null);
  const colsOpen = Boolean(colsAnchorEl);
  const [draftColumnsVisible, setDraftColumnsVisible] = React.useState<Record<string, boolean> | null>(null);
  const [draftOrder, setDraftOrder] = React.useState<string[] | null>(null);

  React.useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === 'object') setColumnWidths((prev) => ({ ...prev, ...parsed }));
      }
    } catch {}
  }, [STORAGE_KEY]);

  React.useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(columnWidths)); } catch {}
  }, [STORAGE_KEY, columnWidths]);

  React.useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!dragCol) return;
      const dx = e.clientX - dragCol.startX;
      const newW = Math.max(16, dragCol.startW + dx);
      setColumnWidths((prev) => ({ ...prev, [dragCol.key]: newW }));
    };
    const onUp = () => setDragCol(null);
    if (dragCol) {
      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp, { once: true } as any);
    }
    return () => {
      window.removeEventListener('mousemove', onMove);
    };
  }, [dragCol]);

  const renderResizableTh = (keyId: string, node: React.ReactNode, align?: 'left'|'right'|'center') => (
    <TableCell align={align} sx={{ ...headerCellSx, width: getColW(keyId), maxWidth: 'none', position: 'relative', userSelect: 'none' }}>
      {node}
      {colResizeMode ? (
        <Box
          role="separator"
          aria-orientation="vertical"
          onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); setDragCol({ key: keyId, startX: e.clientX, startW: columnWidths[keyId] ?? defaultColumnWidths[keyId] ?? 40 }); }}
          sx={{
            position: 'absolute',
            right: 0,
            top: 0,
            width: '12px',
            height: '100%',
            cursor: 'col-resize',
            zIndex: 6,
            touchAction: 'none',
            backgroundImage: 'linear-gradient(to right, transparent 45%, #FF0000 45%, #FF0000 55%, transparent 55%)',
            '&:hover': { backgroundColor: 'rgba(0,0,0,0.06)' }
          }}
        />
      ) : null}
    </TableCell>
  );

  // Sortowanie
  type Order = 'asc' | 'desc';
  const [orderBy, setOrderBy] = React.useState<string>('created_at');
  const [order, setOrder] = React.useState<Order>('desc');
  const handleRequestSort = (property: string) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Styl sortowania
  const sortLabelBaseSx = {
    fontFamily: 'Roboto, sans-serif',
    fontWeight: 400,
    fontSize: '14px',
    color: '#2D2D32',
    display: 'inline-flex',
    alignItems: 'center',
    flexDirection: 'row',
    gap: '4px',
    '& .MuiTableSortLabel-icon': {
      opacity: 1,
      width: 12,
      height: 12,
      ml: '4px'
    }
  } as const;
  const StrongSortIcon = () => (<UnfoldMore sx={{ width: 12, height: 12, color: '#2D2D32' }} />);
  const MutedSortIcon = () => (<UnfoldMore sx={{ width: 12, height: 12, color: '#d4d4d9' }} />);
  const getSortLabelProps = (isActive: boolean) => ({
    IconComponent: (isActive ? StrongSortIcon : MutedSortIcon) as any,
    sx: {
      ...sortLabelBaseSx,
      '& .MuiTableSortLabel-icon': {
        ...(sortLabelBaseSx as any)['& .MuiTableSortLabel-icon'],
        color: isActive ? '#2D2D32' : '#d4d4d9'
      }
    }
  }) as const;

  const headerCellSx = {
    fontFamily: 'Roboto, sans-serif',
    fontSize: '14px',
    fontWeight: 400,
    color: '#2D2D32',
    backgroundColor: '#FAFAFA',
    borderRight: '1px solid #d4d4d9',
    padding: '1px 4px',
  };

  // Pobieranie projektów
  React.useEffect(() => {
    const loadProjects = async () => {
      try {
        const projs = await getAllProjects();
        setProjects(projs);
      } catch (error) {
        console.error('Błąd podczas pobierania projektów:', error);
      }
    };
    loadProjects();
  }, []);

  // Placeholder danych za użytkownika
  const expenses: Expense[] = [
    {
      id: 'exp-1',
      document_date: '2025-11-10',
      received_date: '2025-11-09',
      created_at: '2025-11-11',
      project_id: 'S13',
      project_name: 'LAMINAT',
      contractor_name: 'J. I B. CHOJNOWSCY SPÓŁKA JAWNA',
      net_amount: 9448.38,
      gross_amount: 11621.51,
      remarks: 'Materiały zgodne z projektem',
      status: 'pending',
    },
    {
      id: 'exp-2',
      document_date: '2025-11-12',
      received_date: '2025-11-12',
      created_at: '2025-11-13',
      project_id: 'S14',
      project_name: 'SERWIS',
      contractor_name: 'FIRMA TRANSPORTOWA SP. Z O.O.',
      net_amount: 189.9,
      gross_amount: 233.58,
      remarks: 'Transport materiałów na budowę',
      status: 'approved',
    },
    {
      id: 'exp-3',
      document_date: '2025-11-14',
      received_date: '2025-11-14',
      created_at: '2025-11-15',
      project_id: 'S15',
      project_name: 'MATERIAŁY',
      contractor_name: 'HOTEL CENTRALNY',
      net_amount: 420.0,
      gross_amount: 516.6,
      remarks: '',
      status: 'approved',
    },
    {
      id: 'exp-4',
      document_date: '2025-11-15',
      received_date: '2025-11-16',
      created_at: '2025-11-17',
      project_id: 'S16',
      project_name: 'USŁUGI',
      contractor_name: 'STACJA BENZYNOWA',
      net_amount: 250.0,
      gross_amount: 307.5,
      remarks: 'Paliwo do pojazdów służbowych',
      status: 'rejected',
    },
  ];

  // Filtrowanie
  const filtered = expenses.filter((e) => {
    const q = (searchText || '').toLowerCase();
    const matchesSearch = !q || (
      e.project_id.toLowerCase().includes(q) ||
      e.project_name.toLowerCase().includes(q) ||
      e.contractor_name.toLowerCase().includes(q) ||
      e.document_date.toLowerCase().includes(q) ||
      currency(e.net_amount).toLowerCase().includes(q)
    );
    const matchesProject = !projectFilter || e.project_id === projectFilter;
    const matchesDateFrom = !dateFrom || e.document_date >= dateFrom;
    const matchesDateTo = !dateTo || e.document_date <= dateTo;
    return matchesSearch && matchesProject && matchesDateFrom && matchesDateTo;
  });

  // Podział na zakładki
  const allExpenses = React.useMemo(() => filtered, [filtered]);
  const pendingExpenses = React.useMemo(() => filtered.filter(e => e.status === 'pending'), [filtered]);
  const approvedExpenses = React.useMemo(() => filtered.filter(e => e.status === 'approved'), [filtered]);

  // Sortowanie
  const sortRows = React.useCallback((list: Expense[]) => {
    const copy = [...list];
    copy.sort((a: Expense, b: Expense) => {
      const aVal = (a as any)[orderBy];
      const bVal = (b as any)[orderBy];
      let cmp = 0;
      if (!isNaN(Number(aVal)) && !isNaN(Number(bVal))) cmp = Number(aVal) - Number(bVal);
      else cmp = String(aVal || '').localeCompare(String(bVal || ''));
      return order === 'asc' ? cmp : -cmp;
    });
    return copy;
  }, [order, orderBy]);

  const sortedAll = React.useMemo(() => sortRows(allExpenses), [sortRows, allExpenses]);
  const sortedPending = React.useMemo(() => sortRows(pendingExpenses), [sortRows, pendingExpenses]);
  const sortedApproved = React.useMemo(() => sortRows(approvedExpenses), [sortRows, approvedExpenses]);
  const rowsForTab = React.useMemo(() => {
    if (expensesTab === 0) return sortedAll;
    if (expensesTab === 1) return sortedPending;
    return sortedApproved;
  }, [expensesTab, sortedAll, sortedPending, sortedApproved]);

  // Obliczanie opóźnień dat
  const calculateDelayDays = (docDate: string, recDate: string) => {
    const toUTC = (iso?: string) => {
      if (!iso) return NaN as any;
      const d = new Date(String(iso));
      return Date.UTC(d.getFullYear(), d.getMonth(), d.getDate());
    };
    const d1 = toUTC(docDate);
    const d2 = toUTC(recDate);
    if (isNaN(d1) || isNaN(d2)) return 0;
    const ms = d2 - d1;
    return Math.max(0, Math.floor(ms / (1000 * 60 * 60 * 24)));
  };

  const calculateIntroduceDelayDays = (created: string, docDate: string) => {
    const toUTC = (iso?: string) => {
      if (!iso) return NaN as any;
      const d = new Date(String(iso));
      return Date.UTC(d.getFullYear(), d.getMonth(), d.getDate());
    };
    const createdUTC = toUTC(created);
    const documentDateUTC = toUTC(docDate);
    if (isNaN(createdUTC) || isNaN(documentDateUTC)) return 0;
    const diff = createdUTC - documentDateUTC;
    return Math.max(0, Math.floor(diff / (1000 * 60 * 60 * 24)));
  };

  // Ikona statusu
  const getStatusIcon = (status: Expense['status']) => {
    if (status === 'approved') return <CheckCircle sx={{ fontSize: 18, color: 'success.main' }} />;
    if (status === 'rejected') return <DoNotDisturbOn sx={{ fontSize: 18, color: 'error.main' }} />;
    return <InfoOutlined sx={{ fontSize: 18, color: 'info.main' }} />;
  };

  // Obliczanie salda
  const currentBalance = React.useMemo(() => {
    const totalExpenses = rowsForTab.reduce((sum, e) => sum + e.gross_amount, 0);
    return initialBalance - totalExpenses;
  }, [initialBalance, rowsForTab]);
  
  const balanceFormatted = React.useMemo(
    () => new Intl.NumberFormat('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(currentBalance),
    [currentBalance]
  );

  return (
    <Box sx={{ bgcolor: '#FAFAFA', mt: '20px', position: 'relative' }}>
      {/* Wróć do strony głównej */}
      <Box
        onClick={() => navigate('/')}
        sx={{
          position: 'absolute',
          top: '-24px',
          left: 0,
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px',
          cursor: 'pointer',
          userSelect: 'none'
        }}
        aria-label="Wróć do strony głównej"
        role="button"
      >
        <KeyboardArrowDown sx={{ transform: 'rotate(90deg)', width: 16, height: 16, color: '#1F1FB0' }} />
        <Typography
          sx={{
            fontFamily: 'Roboto, sans-serif',
            fontSize: '14px',
            lineHeight: '16px',
            fontWeight: 500,
            color: '#1F1FB0'
          }}
        >
          Wróć do strony głównej
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mt: 0, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography
            component="h1"
            sx={{
              fontFamily: 'Roboto, sans-serif',
              fontSize: '20px',
              lineHeight: '24px',
              fontWeight: 400,
              color: '#020242',
              m: 0,
              mt: '8.5px'
            }}
          >
            Rejestr wydatków
          </Typography>
          <Chip
            label={`SALDO: ${balanceFormatted} PLN`}
            size="small"
            sx={{
              bgcolor: '#FEEED5',
              color: '#C53030',
              borderRadius: '4px',
              minHeight: 28,
              height: 28,
              mt: '8.5px',
              '& .MuiChip-label': {
                px: 1,
                py: 0,
                fontFamily: 'Roboto, sans-serif',
                fontSize: '14px',
                fontWeight: 500,
                lineHeight: '16px',
                color: '#C53030'
              }
            }}
          />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton
            size="small"
            onClick={() => setInitialBalanceSettingsOpen(true)}
            data-action="settings"
            sx={{ p: 0.25, width: 32, height: 32 }}
            aria-label="Ustawienia salda początkowego"
          >
            <Box 
              component="img" 
              src="/assets/icons/settings_24dp_5F6368_FILL0_wght400_GRAD0_opsz24.svg" 
              alt="Ustawienia salda początkowego"
              sx={{ filter: 'brightness(0) saturate(100%)', width: '100%', height: '100%' }}
            />
          </IconButton>
          <Button
            variant="contained"
            onClick={() => setAddExpenseDialogOpen(true)}
            data-action="add"
            data-hotkey-add="true"
            sx={{
              width: 116,
              height: 40,
              borderRadius: '4px',
              textTransform: 'none',
              fontSize: '14px',
              fontWeight: 600,
              whiteSpace: 'nowrap',
              minWidth: 'auto',
              backgroundColor: '#4040DB',
              color: '#fff',
              '&:hover': { backgroundColor: '#1F1FB0' },
              '&:active': { backgroundColor: '#1A1A98' }
            }}
          >
            Dodaj wydatek
          </Button>
        </Box>
      </Box>

      {/* Filtry */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '300px 300px 200px 200px',
          columnGap: '24px',
          alignItems: 'start',
          height: 56,
          mb: 2,
          bgcolor: '#FAFAFA'
        }}
      >
        {/* Szukaj wydatku */}
        <TextField
          size="small"
          placeholder="Szukaj wydatku"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          variant="outlined"
          inputProps={{
            'data-role': 'page-filter',
            onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
              if (e.key === 'Escape') {
                e.preventDefault();
                e.stopPropagation();
                try { (e.currentTarget as HTMLInputElement).blur(); } catch { }
              }
            }
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ width: 20, height: 20, color: '#595966' }} />
              </InputAdornment>
            )
          }}
          sx={{
            width: 300,
            mt: '20px',
            '& .MuiOutlinedInput-root': {
              height: 36,
              backgroundColor: '#fff',
              borderRadius: '4px'
            },
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: '#d4d4d9'
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#595966'
            },
            '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#595966 !important'
            },
            '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: 'none', outline: 'none' },
            '& .MuiInputBase-input': { px: '12px', fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: '#2D2D32' },
            '& .MuiInputBase-input::placeholder': { color: '#B9B9B9', opacity: 1 }
          }}
        />

        {/* Projekt */}
        <Box sx={{ width: 300, display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '16px', color: '#2D2D32' }}>Projekt</Typography>
          <Select
            size="small"
            value={projectFilter}
            onChange={(e) => setProjectFilter(e.target.value)}
            displayEmpty
            renderValue={projectFilter !== '' ? undefined : () => 'Wybierz z listy'}
            IconComponent={(iconProps: any) => (
              <Box
                component="img"
                alt="dropdown"
                src="/assets/filters/keyboard_arrow_down.png"
                {...iconProps}
                sx={{ width: 18, height: 18, mr: '8px', pointerEvents: 'none' }}
              />
            )}
            sx={{
              height: 36,
              pr: '8px',
              '& .MuiSelect-select': {
                display: 'flex', alignItems: 'center', height: 36,
                fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: projectFilter ? '#2D2D32' : '#B9B9B9'
              },
              backgroundColor: '#fff',
              borderRadius: '4px',
              '& fieldset': { borderColor: '#d4d4d9' },
              '&:hover fieldset': { borderColor: '#595966' },
              '& .MuiOutlinedInput-notchedOutline': { transition: 'border-color 0.2s' },
              '&.Mui-focused fieldset, & .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#595966 !important' },
              '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: 'none', outline: 'none' }
            }}
          >
            <MenuItem value="">Wszystkie</MenuItem>
            {projects.map((p) => (
              <MenuItem key={p.project_id} value={p.project_id}>
                {p.project_id} {p.project_name}
              </MenuItem>
            ))}
          </Select>
        </Box>

        {/* Data od */}
        <Box sx={{ width: 200, display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '16px', color: '#2D2D32' }}>Data od</Typography>
          <TextField
            size="small"
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            variant="outlined"
            inputRef={startDateRef}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start" sx={{ cursor: 'pointer' }} onClick={() => { try { (startDateRef.current as any)?.showPicker?.(); } catch { startDateRef.current?.focus(); } }}>
                  <Box component="img" src="/assets/filters/calendar_today.svg" alt="calendar" />
                </InputAdornment>
              )
            }}
            InputLabelProps={{ shrink: true }}
            sx={{
              '& .MuiOutlinedInput-root': { height: 36, backgroundColor: '#fff', borderRadius: '4px' },
              '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d4d4d9' },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#595966' },
              '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#595966 !important' },
              '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: 'none', outline: 'none' },
              '& .MuiInputBase-input': { fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: dateFrom ? '#2D2D32' : '#B9B9B9' },
              '& input::-webkit-calendar-picker-indicator': { opacity: 0, display: 'none' },
              '& input::-ms-clear': { display: 'none' }
            }}
          />
        </Box>

        {/* Data do */}
        <Box sx={{ width: 200, display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '16px', color: '#2D2D32' }}>Data do</Typography>
          <TextField
            size="small"
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            variant="outlined"
            inputRef={endDateRef}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start" sx={{ cursor: 'pointer' }} onClick={() => { try { (endDateRef.current as any)?.showPicker?.(); } catch { endDateRef.current?.focus(); } }}>
                  <Box component="img" src="/assets/filters/calendar_today.svg" alt="calendar" />
                </InputAdornment>
              )
            }}
            InputLabelProps={{ shrink: true }}
            sx={{
              '& .MuiOutlinedInput-root': { height: 36, backgroundColor: '#fff', borderRadius: '4px' },
              '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d4d4d9' },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#595966' },
              '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#595966 !important' },
              '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: 'none', outline: 'none' },
              '& .MuiInputBase-input': { fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: dateTo ? '#2D2D32' : '#B9B9B9' },
              '& input::-webkit-calendar-picker-indicator': { opacity: 0, display: 'none' },
              '& input::-ms-clear': { display: 'none' }
            }}
          />
        </Box>
      </Box>

      {/* Zakładki */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '2px', height: 40, mt: '26.5px', mb: 0 }}>
        <Tabs
          value={expensesTab}
          onChange={(_, v) => setExpensesTab(v)}
          sx={{
            minHeight: 40,
            height: 40,
            flexGrow: 1,
            '& .MuiTabs-indicator': { display: 'none' },
            '& .MuiTabs-flexContainer': { gap: '2px' }
          }}
        >
          <Tab
            disableRipple
            label={`Wszystkie (${allExpenses.length})`}
            sx={{
              textTransform: 'none',
              color: '#020242',
              fontFamily: 'Roboto, sans-serif',
              fontSize: '14px',
              fontWeight: 400,
              px: 3,
              minHeight: 32,
              height: 32,
              mt: '8px',
              bgcolor: '#F1F1F6',
              borderRadius: '4px 4px 0 0',
              '&.Mui-selected': { mt: 0, height: 40, bgcolor: '#E6E6EC', color: '#020242', fontWeight: 500 }
            }}
          />
          <Tab
            disableRipple
            label={`Do uzgodnienia (${pendingExpenses.length})`}
            sx={{
              textTransform: 'none',
              color: '#020242',
              fontFamily: 'Roboto, sans-serif',
              fontSize: '14px',
              fontWeight: 400,
              px: 3,
              minHeight: 32,
              height: 32,
              mt: '8px',
              bgcolor: '#F1F1F6',
              borderRadius: '4px 4px 0 0',
              '&.Mui-selected': { mt: 0, height: 40, bgcolor: '#E6E6EC', color: '#020242', fontWeight: 500 }
            }}
          />
          <Tab
            disableRipple
            label={`Uzgodnione (${approvedExpenses.length})`}
            sx={{
              textTransform: 'none',
              color: '#020242',
              fontFamily: 'Roboto, sans-serif',
              fontSize: '14px',
              fontWeight: 400,
              px: 3,
              minHeight: 32,
              height: 32,
              mt: '8px',
              bgcolor: '#F1F1F6',
              borderRadius: '4px 4px 0 0',
              '&.Mui-selected': { mt: 0, height: 40, bgcolor: '#E6E6EC', color: '#020242', fontWeight: 500 }
            }}
          />
        </Tabs>
        <IconButton
          size="small"
          sx={{
            width: 32,
            height: 32,
            borderRadius: '4px',
            mt: '4px',
            p: 0,
            alignSelf: 'flex-end',
            bgcolor: colsOpen ? '#E0E0F5' : 'transparent',
            transition: 'background-color 0.15s ease',
            '&:hover': { bgcolor: 'rgba(0,0,0,0.06)' }
          }}
          onClick={(e) => {
            setColResizeMode(true);
            setColsAnchorEl(e.currentTarget);
          }}
        >
          <Box component="img" alt="kolumny" src="/assets/icons/frame-344.svg" sx={{ width: 32, height: 32 }} />
        </IconButton>
      </Box>

      {/* Tabela */}
      <TableContainer
        component={Paper}
        sx={{
          height: 432,
          overflow: 'auto',
          position: 'relative',
          border: '1px solid #d0d0d6',
          borderBottom: '1px solid #d0d0d6',
          borderRadius: 0,
          borderTopRightRadius: 4,
          borderBottomLeftRadius: 4,
          borderBottomRightRadius: 4,
          overflowX: 'hidden',
          boxShadow: 'none',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          '&::-webkit-scrollbar': { width: 0, height: 0 }
        }}
        ref={tableContainerRef}
      >
        <Table size="small" stickyHeader sx={{
          tableLayout: 'fixed',
          width: '100%',
          '& .MuiTableCell-root': { padding: '1px 4px', fontSize: '0.85rem', borderRadius: 0, fontFamily: 'Roboto, sans-serif' },
          '& .MuiTableCell-head': { position: 'sticky', top: 0, zIndex: 2, backgroundColor: '#FAFAFA', borderRight: '1px solid #d4d4d9', borderRadius: 0, boxShadow: 'none' },
          '& .MuiTableCell-body': { borderRight: 'none', borderRadius: 0, color: '#2D2D32', fontFamily: 'Roboto, sans-serif' },
          '& .MuiTableRow-head > *:last-child': { borderRight: 'none', borderTopRightRadius: 4 }
        }}>
          <TableHead>
            <TableRow>
              {(draftOrder ?? columnOrder).map((key) => {
                if (!colV(key)) return null;
                if (key === 'dates') {
                  return renderResizableTh('dates', (
                    <TableSortLabel {...getSortLabelProps(orderBy === 'created_at')} active={orderBy === 'created_at'} direction={order} onClick={() => handleRequestSort('created_at')}>
                      Data
                    </TableSortLabel>
                  ));
                }
                if (key === 'project') {
                  return renderResizableTh('project', (
                    <TableSortLabel {...getSortLabelProps(orderBy === 'project_id')} active={orderBy === 'project_id'} direction={order} onClick={() => handleRequestSort('project_id')}>
                      Projekt
                    </TableSortLabel>
                  ));
                }
                if (key === 'contractor') {
                  return renderResizableTh('contractor', (
                    <TableSortLabel {...getSortLabelProps(orderBy === 'contractor_name')} active={orderBy === 'contractor_name'} direction={order} onClick={() => handleRequestSort('contractor_name')}>
                      Kontrahent
                    </TableSortLabel>
                  ));
                }
                if (key === 'net') {
                  return renderResizableTh('net', (
                    <TableSortLabel {...getSortLabelProps(orderBy === 'net_amount')} active={orderBy === 'net_amount'} direction={order} onClick={() => handleRequestSort('net_amount')}>
                      Wartość netto
                    </TableSortLabel>
                  ), 'right');
                }
                if (key === 'gross') {
                  return renderResizableTh('gross', (
                    <TableSortLabel {...getSortLabelProps(orderBy === 'gross_amount')} active={orderBy === 'gross_amount'} direction={order} onClick={() => handleRequestSort('gross_amount')}>
                      Wartość brutto
                    </TableSortLabel>
                  ), 'right');
                }
                if (key === 'remarks') {
                  return renderResizableTh('remarks', 'Uwagi');
                }
                if (key === 'approvals') {
                  return renderResizableTh('approvals', null as any);
                }
                if (key === 'preview') {
                  return renderResizableTh('preview', null as any, 'center');
                }
                if (key === 'actions') {
                  return renderResizableTh('actions', null as any, 'right');
                }
                return null;
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {rowsForTab.length === 0 ? (
              <TableRow>
                <TableCell colSpan={(draftOrder ?? columnOrder).filter(k => colV(k)).length} align="center" sx={{ py: 2, color: 'text.secondary' }}>
                  Brak wydatków do wyświetlenia.
                </TableCell>
              </TableRow>
            ) : (
              rowsForTab.map((exp) => {
                const delayDays = calculateDelayDays(exp.document_date, exp.received_date);
                const introduceDelayDays = calculateIntroduceDelayDays(exp.created_at, exp.document_date);
                return (
                  <TableRow key={exp.id} hover>
                    {(draftOrder ?? columnOrder).map((key) => {
                      if (!colV(key)) return null;
                      if (key === 'dates') {
                        return (
                          <TableCell key={key} sx={{ width: getColW('dates'), whiteSpace: 'nowrap', pr: 2 }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.25 }}>
                              <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', position: 'relative' }}>
                                <Tooltip title={`Data wprowadzenia: ${String(exp.created_at || '')}`} arrow>
                                  <span>{String(exp.created_at || '').slice(0, 10)}</span>
                                </Tooltip>
                                {introduceDelayDays > 0 ? (
                                  <Box component="span" sx={{ ml: 0.5, bgcolor: 'primary.main', color: 'common.white', height: 12, lineHeight: '12px', px: 0.375, borderRadius: '6px', fontSize: 9, fontWeight: 700 }}>{`+${introduceDelayDays}`}</Box>
                                ) : null}
                              </Box>
                              <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', position: 'relative' }}>
                                <Tooltip title={`Data dokumentu: ${String(exp.document_date || '')}`} arrow>
                                  <span>{exp.document_date || ''}</span>
                                </Tooltip>
                                {delayDays > 0 ? (
                                  <Box component="span" sx={{ ml: 0.5, bgcolor: 'error.main', color: 'common.white', height: 12, lineHeight: '12px', px: 0.375, borderRadius: '6px', fontSize: 9, fontWeight: 700 }}>{`+${delayDays}`}</Box>
                                ) : null}
                              </Box>
                            </Box>
                          </TableCell>
                        );
                      }
                      if (key === 'project') {
                        return (
                          <TableCell key={key} sx={{ width: getColW('project'), pl: 0 }}>
                            <Tooltip title={`${exp.project_id} ${exp.project_name || ''}`} arrow>
                              <Box sx={{
                                display: '-webkit-box',
                                WebkitBoxOrient: 'vertical',
                                WebkitLineClamp: 2,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                lineHeight: 1.25,
                                whiteSpace: 'normal',
                                wordBreak: 'break-word',
                                maxHeight: '2.5em'
                              }}>
                                {`${exp.project_id} ${exp.project_name || ''}`}
                              </Box>
                            </Tooltip>
                          </TableCell>
                        );
                      }
                      if (key === 'contractor') {
                        return (
                          <TableCell key={key} sx={{ width: getColW('contractor') }}>
                            <Tooltip title={exp.contractor_name} arrow>
                              <Box sx={{
                                display: '-webkit-box',
                                WebkitBoxOrient: 'vertical',
                                WebkitLineClamp: 2,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                lineHeight: 1.25,
                                whiteSpace: 'normal',
                                wordBreak: 'break-word',
                                maxHeight: '2.5em'
                              }}>
                                {exp.contractor_name}
                              </Box>
                            </Tooltip>
                          </TableCell>
                        );
                      }
                      if (key === 'net') {
                        return <TableCell key={key} align="right" sx={{ width: getColW('net'), whiteSpace: 'nowrap' }}>{currency(exp.net_amount)}</TableCell>;
                      }
                      if (key === 'gross') {
                        return <TableCell key={key} align="right" sx={{ width: getColW('gross'), whiteSpace: 'nowrap' }}>{currency(exp.gross_amount)}</TableCell>;
                      }
                      if (key === 'remarks') {
                        return (
                          <TableCell key={key} sx={{ width: getColW('remarks') }}>
                            <Tooltip title={exp.remarks || ''} arrow>
                              <Box sx={{
                                display: '-webkit-box',
                                WebkitBoxOrient: 'vertical',
                                WebkitLineClamp: 2,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                lineHeight: 1.25,
                                whiteSpace: 'normal',
                                wordBreak: 'break-word',
                                maxHeight: '2.5em'
                              }}>
                                {exp.remarks || ''}
                              </Box>
                            </Tooltip>
                          </TableCell>
                        );
                      }
                      if (key === 'approvals') {
                        return (
                          <TableCell key={key} sx={{ p: 0.5, width: getColW('approvals') }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 0.5 }}>
                              <Tooltip title={exp.status === 'approved' ? 'Uzgodnione' : exp.status === 'rejected' ? 'Odrzucone' : 'Do uzgodnienia'} arrow>
                                {getStatusIcon(exp.status)}
                              </Tooltip>
                              {exp.google_drive_file_url ? (
                                <Tooltip title="Skopiuj link do PDF" arrow>
                                  <IconButton size="small" onClick={() => { navigator.clipboard.writeText(String(exp.google_drive_file_url)); }}>
                                    <ContentCopy fontSize="inherit" />
                                  </IconButton>
                                </Tooltip>
                              ) : null}
                            </Box>
                          </TableCell>
                        );
                      }
                      if (key === 'preview') {
                        return (
                          <TableCell key={key} align="center" sx={{ width: getColW('preview') }}>
                            <IconButton size="small" color="primary" title="Podgląd PDF" onClick={() => { /* TODO: Podgląd */ }}>
                              <Visibility fontSize="small" />
                            </IconButton>
                          </TableCell>
                        );
                      }
                      if (key === 'actions') {
                        return (
                          <TableCell key={key} align="right" sx={{ width: getColW('actions'), position: 'relative' }}>
                            <IconButton size="small" onClick={(e) => { e.stopPropagation(); setMenuRow(exp); setMenuPosition({ top: e.clientY, left: e.clientX }); }}>
                              <MoreVert fontSize="small" />
                            </IconButton>
                          </TableCell>
                        );
                      }
                      return null;
                    })}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Okno „Dostosuj kolumny" */}
      <Backdrop open={colsOpen} sx={{ color: '#fff', zIndex: (theme: any) => theme.zIndex.modal + 1, backgroundColor: 'rgba(0,0,0,0.35)', pointerEvents: 'none' }}>
        <Paper
          elevation={8}
          sx={{
            position: 'fixed',
            bottom: 0,
            right: 0,
            height: '50vh',
            width: '280px',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0px 5px 30px rgba(0,0,0,0.15)',
            borderRadius: 0,
            zIndex: (theme) => theme.zIndex.modal + 2,
            backgroundColor: '#fff',
            pointerEvents: 'auto'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <Box sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flex: '0 0 auto', position: 'sticky', top: 0, bgcolor: '#FFFFFF', zIndex: 1 }}>
            <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: 16, color: '#2D2D32' }}>Dostosuj kolumny</Typography>
            <IconButton size="small" onClick={() => { setColsAnchorEl(null); setDraftColumnsVisible(null); setDraftOrder(null); setColResizeMode(false); }}>
              <Close fontSize="small" />
            </IconButton>
          </Box>
          <Divider />
          <Box sx={{ px: 0, py: 1.5, flex: '1 1 auto', minHeight: 0, overflowY: 'auto' }}>
            {(() => {
              const list = draftColumnsVisible ?? columnsVisible;
              if (draftColumnsVisible == null) setDraftColumnsVisible({ ...columnsVisible });
              const ord = draftOrder ?? columnOrder;
              if (draftOrder == null) setDraftOrder([...columnOrder]);
              return ord.map((key) => (
                <Box
                  key={key}
                  sx={{ py: 1, px: 2 }}
                  draggable
                  onDragStart={(e) => { e.dataTransfer.setData('text/plain', key); }}
                  onDragOver={(e) => { e.preventDefault(); }}
                  onDrop={(e) => {
                    e.preventDefault();
                    const src = e.dataTransfer.getData('text/plain');
                    if (!src || src === key) return;
                    setDraftOrder((prev) => {
                      const cur = [...(prev || ord)];
                      const from = cur.indexOf(src);
                      const to = cur.indexOf(key);
                      if (from < 0 || to < 0) return cur;
                      cur.splice(to, 0, cur.splice(from, 1)[0]);
                      return cur;
                    });
                  }}
                >
                  <Box sx={{ border: '1px solid #d4d4d9', borderRadius: '4px', height: 36, width: 257, display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1.25 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                      <IconButton onClick={() => setDraftColumnsVisible((prev) => ({ ...(prev || list), [key]: !((prev || list)[key] !== false) }))} size="small" sx={{ p: 0 }} aria-label="toggle column">
                        <Box component="img" src={(draftColumnsVisible ?? columnsVisible)[key] !== false ? '/assets/icons/checkbox-on.svg' : '/assets/icons/checkbox-off.svg'} alt="check" sx={{ width: 23, height: 24 }} />
                      </IconButton>
                      <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: 14, color: '#2D2D32' }}>
                        {key === 'dates' ? 'DATA' :
                         key === 'project' ? 'PROJEKT' :
                         key === 'contractor' ? 'KONTRAHENT' :
                         key === 'net' ? 'WARTOŚĆ NETTO' :
                         key === 'gross' ? 'WARTOŚĆ BRUTTO' :
                         key === 'remarks' ? 'UWAGI' :
                         key === 'approvals' ? 'UZGODNIENIE' :
                         key === 'preview' ? 'PODGLĄD' :
                         key === 'actions' ? 'AKCJE' : key.toUpperCase()}
                      </Typography>
                    </Box>
                    <DragIndicator fontSize="small" sx={{ color: '#595966', cursor: 'grab' }} />
                  </Box>
                </Box>
              ));
            })()}
          </Box>
          <Divider />
          <Box sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '24px', flex: '0 0 auto', boxShadow: '0px 4px 4px rgba(0,0,0,0.05)', position: 'sticky', bottom: 0, bgcolor: '#FFFFFF', zIndex: 1 }}>
            <Button variant="text" onClick={() => { setColsAnchorEl(null); setDraftColumnsVisible(null); setDraftOrder(null); setColResizeMode(false); }} sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 500, fontSize: 14, color: '#2D2D32', textTransform: 'none' }}>Anuluj</Button>
            <Button
              variant="contained"
              sx={{ width: 123, height: 40, borderRadius: '4px', textTransform: 'none', fontFamily: 'Roboto, sans-serif', fontWeight: 600, fontSize: 14, backgroundColor: '#020242', '&:hover': { backgroundColor: '#020242' } }}
              onClick={() => {
                const vis = draftColumnsVisible ?? columnsVisible;
                setColumnsVisible(vis);
                try { localStorage.setItem(VISIBLE_KEY, JSON.stringify(vis)); } catch {}
                if (draftOrder) {
                  setColumnOrder(draftOrder);
                  try { localStorage.setItem(ORDER_KEY, JSON.stringify(draftOrder)); } catch {}
                }
                setColsAnchorEl(null);
                setDraftColumnsVisible(null);
                setDraftOrder(null);
                setColResizeMode(false);
              }}
            >
              Zapisz zmiany
            </Button>
          </Box>
        </Paper>
      </Backdrop>

      {/* Menu kontekstowe */}
      <Menu
        open={Boolean(menuPosition)}
        onClose={() => setMenuPosition(null)}
        anchorReference="anchorPosition"
        anchorPosition={menuPosition ? { top: menuPosition.top, left: menuPosition.left } : undefined}
      >
        <MenuItem onClick={() => { setMenuPosition(null); /* TODO: Edytuj */ }}>
          Edytuj
        </MenuItem>
        <MenuItem onClick={() => { setMenuPosition(null); /* TODO: Usuń */ }}>
          Usuń
        </MenuItem>
      </Menu>

      {/* Dialog dodawania wydatku */}
      <AddExpenseDialog
        open={addExpenseDialogOpen}
        onClose={() => setAddExpenseDialogOpen(false)}
        onSave={(data) => {
          // TODO: Implementacja zapisywania wydatku
          console.log('Zapisano wydatek:', data);
        }}
      />

      {/* Panel ustawień salda początkowego */}
      <InitialBalanceSettingsDrawer
        open={initialBalanceSettingsOpen}
        onClose={() => setInitialBalanceSettingsOpen(false)}
        initialBalance={initialBalance}
        initialBalanceDate={initialBalanceDate}
        onSave={async (balance: number, date: string) => {
          // TODO: Implementacja zapisywania salda początkowego do bazy danych
          setInitialBalance(balance);
          setInitialBalanceDate(date);
          console.log('Zapisano saldo początkowe:', { balance, date });
        }}
      />
    </Box>
  );
};

export default ExpensesPage;
